import { useState, type FormEvent, type KeyboardEvent, useRef, useEffect } from "react";
import { ArrowRight } from "./icons";

interface ChatInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
  showFinish?: boolean;
  onFinish?: () => void;
  isFinishing?: boolean;
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = "Start writing or finish your reflection...",
  showFinish = false,
  onFinish,
  isFinishing = false,
}: ChatInputProps) {
  const [content, setContent] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [content]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (content.trim() && !disabled) {
      onSend(content.trim());
      setContent("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const hasContent = content.trim().length > 0;

  return (
    <form onSubmit={handleSubmit} className="px-4 pt-4 pb-safe">
      <div className="flex items-end gap-4">
        {/* Text input area */}
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className="w-full resize-none py-2 text-text-primary placeholder:text-text-placeholder focus:outline-none disabled:text-text-placeholder bg-transparent text-base"
            style={{ minHeight: "40px" }}
          />
        </div>

        {/* Show Finish button when there's content and showFinish is true, otherwise show Send */}
        {showFinish && !hasContent ? (
          <button
            type="button"
            onClick={onFinish}
            disabled={isFinishing}
            className="h-10 px-6 rounded-full bg-text-primary text-white flex items-center justify-center shrink-0 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {isFinishing ? "..." : "Finish"}
          </button>
        ) : (
          <button
            type="submit"
            disabled={disabled || !hasContent}
            className="w-10 h-10 rounded-full bg-text-primary text-white flex items-center justify-center disabled:bg-border-disabled-subtle disabled:cursor-not-allowed shrink-0 transition-colors"
            aria-label="Send message"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </form>
  );
}
